import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchDealerPlacementImageUrl, useAddDealerPlacement, useDealerPlacements } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { DealerPlacement } from '../../types';
import { apiDate, date, money, percent } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import PendingDocumentPicker from '../investor/PendingDocumentPicker';
import TransactionImageLink from '../investor/TransactionImageLink';
import TransactionAccountField from '../common/TransactionAccountField';
import { useTransactionAccountField } from '../../utils/transactionAccountField';
import QuickShareButton from '../common/QuickShareButton';
import { usePartyContact } from '../common/usePartyContact';
import { transactionMessage } from '../../utils/quickShare';
import { useTranslation } from 'react-i18next';
import AutoText from '../common/AutoText';

/** Countdown tag for one tranche's expected return date — same red/orange/blue scheme used elsewhere. */
function DueTag({ days, overdue }: { days: number | null; overdue: boolean }) {
  if (days === null) return <span style={{ color: '#B8BCC9' }}>—</span>;
  if (overdue) return <Tag color="red">{Math.abs(days)} days overdue</Tag>;
  return <Tag color={days <= 7 ? 'orange' : days <= 30 ? 'gold' : 'blue'}>{days} days left</Tag>;
}

/** Mirrors the investor Investments tab: one row per tranche of money given to the dealer. */
export default function DealerInvestmentsTab({ dealerId }: { dealerId: number }) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canWrite = hasRole('Admin');
  const { data, isLoading } = useDealerPlacements(dealerId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addPlacement = useAddDealerPlacement(dealerId);
  const accountField = useTransactionAccountField('Dealer', dealerId);
  const contact = usePartyContact('Dealer', dealerId);
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addPlacement.mutateAsync({
        amount: values.amount as number,
        profitRate: values.profitRate as number,
        placementDate: apiDate(values.placementDate as dayjs.Dayjs)!,
        expectedReturnDate: apiDate(values.expectedReturnDate as dayjs.Dayjs | undefined),
        notes: values.notes as string | undefined,
        paymentMethod: values.paymentMethod as string | undefined,
        transactionImage,
      });
      await accountField.commitIfChanged();
      message.success('Investment added.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ placementDate: dayjs() });
              setOpen(true);
            }}
          >
            Add investment
          </Button>
        </Space>
      )}

      <Table<DealerPlacement>
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        summary={(rows) => (
          <Table.Summary.Row>
            {/* #, Date, Amount, Withdrawn — so the total lands under Remaining, which is what it is. */}
            <Table.Summary.Cell index={0} colSpan={4}>
              <strong>Total Investment</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              <strong>{money(rows.reduce((sum, r) => sum + r.remainingAmount, 0))}</strong>
            </Table.Summary.Cell>
            {/* Rate, Status, Due date, Payment method, Notes, Transaction proof, Receipt. */}
            <Table.Summary.Cell index={5} colSpan={7} />
          </Table.Summary.Row>
        )}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: t('common.date'), dataIndex: 'placementDate', render: date },
          { title: t('common.amount'), dataIndex: 'amount', align: 'right', render: money },
          { title: t('finance.withdrawn'), dataIndex: 'returnedAmount', align: 'right', render: money },
          { title: t('finance.remaining'), dataIndex: 'remainingAmount', align: 'right', render: money },
          { title: t('table.rate'), dataIndex: 'profitRate', align: 'right', render: percent },
          {
            title: t('common.status'),
            dataIndex: 'isActive',
            render: (active: boolean) => <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Closed'}</Tag>,
          },
          {
            title: 'Due date',
            render: (_, row) => <DueTag days={row.daysRemaining} overdue={row.isOverdue} />,
          },
          { title: t('finance.paymentMethod'), dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
          { title: t('common.notes'), dataIndex: 'notes', render: (v: string | null) => <AutoText value={v} /> },
          {
            title: t('finance.transactionProof'),
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — tranche #${row.id}`}
                fetchUrl={() => fetchDealerPlacementImageUrl(dealerId, row.id)}
              />
            ),
          },
          {
            title: t('finance.receipt'),
            render: (_, row) => (
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadFile(`/receipts/dealer-investment/${row.id}`, `investment_receipt_${row.id}.pdf`).catch(
                    (err) => message.error(errorMessage(err)),
                  )
                }
              >
                PDF
              </Button>
            ),
          },
          ...(contact.ready
            ? [
                {
                  title: t('common.share'),
                  render: (_: unknown, row: DealerPlacement) => (
                    <QuickShareButton
                      phone={contact.phone}
                      recipientName={contact.name}
                      message={transactionMessage(contact, 'New investment', row.amount, row.placementDate)}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal maskClosable={false}
        title="Add investment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addPlacement.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item name="amount" label={t('finance.amountPkr')} rules={[{ required: true }, { type: 'number', min: 0.01 }]}>
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="profitRate" label={t('finance.monthlyProfitRate')} rules={[{ required: true }]}>
            <InputNumber min={0} max={99.99} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="placementDate" label={t('finance.investmentDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="expectedReturnDate" label={t('forms.profitExpectedOn')} help="Used for the due-date countdown.">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="paymentMethod" label={t('finance.paymentMethod')}>
            <Select placeholder="How the money was handed over" allowClear options={paymentMethodOptions} />
          </Form.Item>
          <TransactionAccountField field={accountField} paymentMethod={paymentMethod} />
          <Form.Item name="notes" label={t('common.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="transaction-image"
            label={t('finance.transactionProofOptional')}
            hint="Bank slip, JazzCash/EasyPaisa screenshot or cash voucher · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
